import React, { useState, useEffect, useRef } from 'react';
import { Terminal, X, Copy, Trash2, Power, Database, Heart, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';

const AdminDevTools = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [logs, setLogs] = useState(() => {
    const savedLogs = localStorage.getItem('admin_console_logs');
    return savedLogs ? JSON.parse(savedLogs) : [];
  });
  const [pwaEnabled, setPwaEnabled] = useState(true);
  const [errorCount, setErrorCount] = useState(() => {
    const savedLogs = localStorage.getItem('admin_console_logs');
    if (savedLogs) {
      const parsedLogs = JSON.parse(savedLogs);
      return parsedLogs.filter(log => log.type === 'error').length;
    }
    return 0;
  });
  const logsEndRef = useRef(null);

  // Check if admin is logged in and load persisted logs
  useEffect(() => {
    const checkAdmin = () => {
      const isAdmin = localStorage.getItem('adminAuth') === 'true';
      setIsVisible(isAdmin);
      
      // Load persisted logs when admin logs in
      if (isAdmin) {
        const savedLogs = localStorage.getItem('admin_console_logs');
        if (savedLogs) {
          const parsedLogs = JSON.parse(savedLogs);
          setLogs(parsedLogs);
          setErrorCount(parsedLogs.filter(log => log.type === 'error').length);
          console.log(`📋 [AdminDevTools] Loaded ${parsedLogs.length} persisted logs (${parsedLogs.filter(log => log.type === 'error').length} errors)`);
        }
      }
    };

    checkAdmin();
    const interval = setInterval(checkAdmin, 2000);
    return () => clearInterval(interval);
  }, []);

  // Enhanced error capture - ALL types of errors
  useEffect(() => {
    if (!isVisible) return;

    const addLog = (type, message, source = '') => {
      const timestamp = new Date().toLocaleTimeString();
      const formattedMessage = typeof message === 'object' ? JSON.stringify(message, null, 2) : String(message);
      
      setLogs(prev => {
        const newLogs = [...prev.slice(-99), { type, message: formattedMessage, timestamp, source }];
        localStorage.setItem('admin_console_logs', JSON.stringify(newLogs));
        return newLogs;
      });

      if (type === 'error') {
        setErrorCount(prev => prev + 1);
      }
    };

    // 1. Console methods override
    const originalLog = console.log;
    const originalError = console.error;
    const originalWarn = console.warn;

    console.log = (...args) => {
      originalLog.apply(console, args);
      const message = args.map(arg => 
        typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
      ).join(' ');
      addLog('log', message, 'console');
    };

    console.error = (...args) => {
      originalError.apply(console, args);
      const message = args.map(arg => 
        typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
      ).join(' ');
      addLog('error', message, 'console');
    };

    console.warn = (...args) => {
      originalWarn.apply(console, args);
      const message = args.map(arg => 
        typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
      ).join(' ');
      addLog('warn', message, 'console');
    };

    // 2. Window error handler - catches runtime errors
    const handleWindowError = (event) => {
      const errorMessage = `${event.message} at ${event.filename}:${event.lineno}:${event.colno}`;
      addLog('error', errorMessage, 'window');
      return false; // Don't prevent default error handling
    };

    // 3. Unhandled promise rejections
    const handleUnhandledRejection = (event) => {
      const reason = event.reason;
      let errorMessage = 'Unhandled Promise Rejection: ';
      
      if (reason instanceof Error) {
        errorMessage += `${reason.message}\nStack: ${reason.stack}`;
      } else if (typeof reason === 'object') {
        errorMessage += JSON.stringify(reason, null, 2);
      } else {
        errorMessage += String(reason);
      }
      
      addLog('error', errorMessage, 'promise');
    };

    // 4. Network error interceptor (fetch)
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const url = typeof args[0] === 'string' ? args[0] : args[0].url;
      
      try {
        const response = await originalFetch(...args);
        
        // Log failed HTTP requests
        if (!response.ok) {
          addLog('error', `HTTP ${response.status}: ${response.statusText} - ${url}`, 'network');
        }
        
        return response;
      } catch (error) {
        addLog('error', `Network Error: ${error.message} - ${url}`, 'network');
        throw error;
      }
    };

    // Attach error listeners
    window.addEventListener('error', handleWindowError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    // Cleanup
    return () => {
      console.log = originalLog;
      console.error = originalError;
      console.warn = originalWarn;
      window.fetch = originalFetch;
      window.removeEventListener('error', handleWindowError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [isVisible]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs]);

  const clearLogs = () => {
    if (logs.length > 0 && !window.confirm(`Clear all ${logs.length} logs? This cannot be undone.`)) {
      return;
    }
    setLogs([]);
    setErrorCount(0);
    localStorage.removeItem('admin_console_logs');
    console.log('🗑️ [AdminDevTools] All logs cleared');
  };

  const copyLogs = () => {
    const logText = logs.map(log => 
      `[${log.timestamp}] [${log.source || 'unknown'}] ${log.type.toUpperCase()}: ${log.message}`
    ).join('\n');
    navigator.clipboard.writeText(logText);
    console.log('📋 Logs copied to clipboard!');
  };

  const handleTogglePWA = async () => {
    if (!pwaEnabled) {
      setPwaEnabled(true);
      alert('PWA Enabled! Refreshing...');
      window.location.reload();
    } else {
      if (!window.confirm('Disable PWA? Service worker will be unregistered.')) return;
      
      try {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map(reg => reg.unregister()));
        
        setPwaEnabled(false);
        alert('✅ PWA Disabled!');
      } catch (error) {
        console.error('Error disabling PWA:', error);
        alert('❌ Failed to disable PWA');
      }
    }
  };

  const handleClearCache = async () => {
    if (!window.confirm('Clear all PWA caches?')) return;
    
    try {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(name => caches.delete(name)));
      
      alert('✅ Cache cleared! Refresh recommended.');
    } catch (error) {
      console.error('Error clearing cache:', error);
      alert('❌ Failed to clear cache');
    }
  };

  const refreshPage = () => {
    window.location.reload();
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Floating Dev Tools Button */}
      {!isExpanded && (
        <button
          onClick={() => setIsExpanded(true)}
          className="fixed bottom-4 right-4 z-[9999] bg-gradient-to-r from-purple-600 to-blue-600 text-white p-3 rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-110 relative"
          title="Open Dev Tools"
        >
          <Terminal className="w-5 h-5" />
          {errorCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
              {errorCount > 9 ? '9+' : errorCount}
            </span>
          )}
        </button>
      )}

      {/* Dev Tools Panel */}
      {isExpanded && (
        <div className="fixed bottom-0 right-0 w-full md:w-2/3 lg:w-1/2 h-96 bg-gray-900 text-white shadow-2xl z-[9999] flex flex-col border-t-4 border-purple-500">
          {/* Header */}
          <div className="flex items-center justify-between p-3 bg-gray-800 border-b border-gray-700">
            <div className="flex items-center gap-2">
              <Terminal className="w-5 h-5 text-purple-400" />
              <span className="font-bold text-sm">Admin Dev Console</span>
              {errorCount > 0 && (
                <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-bold">
                  {errorCount} errors
                </span>
              )}
              <span className="text-xs text-gray-500 ml-2 hidden sm:flex items-center gap-1">
                Made with <Heart className="w-3 h-3 text-red-500 fill-red-500 animate-pulse" /> by Prince
              </span>
            </div>
            
            <div className="flex items-center gap-2">
              {/* Refresh */}
              <button
                onClick={refreshPage}
                className="px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-medium transition-colors flex items-center gap-1"
                title="Refresh Page"
              >
                <RefreshCw className="w-3 h-3" />
                <span className="hidden sm:inline">Refresh</span>
              </button>
              
              {/* PWA Toggle */}
              <button
                onClick={handleTogglePWA}
                className={`px-2 py-1 rounded text-xs font-medium transition-colors flex items-center gap-1 ${
                  pwaEnabled 
                    ? 'bg-green-600 hover:bg-green-700 text-white' 
                    : 'bg-red-600 hover:bg-red-700 text-white'
                }`}
                title="Toggle PWA"
              >
                <Power className="w-3 h-3" />
                <span className="hidden sm:inline">PWA {pwaEnabled ? 'ON' : 'OFF'}</span>
              </button>
              
              {/* Clear Cache */}
              <button
                onClick={handleClearCache}
                className="px-2 py-1 bg-orange-600 hover:bg-orange-700 text-white rounded text-xs font-medium transition-colors flex items-center gap-1"
                title="Clear Cache"
              >
                <Database className="w-3 h-3" />
                <span className="hidden sm:inline">Clear</span>
              </button>
              
              <Button
                size="sm"
                variant="ghost"
                onClick={copyLogs}
                className="text-xs text-gray-300 hover:text-white"
              >
                <Copy className="w-4 h-4" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={clearLogs}
                className="text-xs text-gray-300 hover:text-white"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
              <button
                onClick={() => setIsExpanded(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Logs Area */}
          <div className="flex-1 overflow-y-auto p-3 space-y-1 font-mono text-xs">
            {logs.length === 0 ? (
              <div className="text-gray-500 text-center py-8">
                <Terminal className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No logs yet...</p>
                <p className="text-xs mt-2">Capturing: console, network, errors, promises</p>
              </div>
            ) : (
              logs.map((log, index) => (
                <div
                  key={index}
                  className={`p-2 rounded ${
                    log.type === 'error'
                      ? 'bg-red-900/20 text-red-300 border-l-4 border-red-500'
                      : log.type === 'warn'
                      ? 'bg-yellow-900/20 text-yellow-300 border-l-4 border-yellow-500'
                      : 'bg-gray-800 text-gray-300'
                  }`}
                >
                  <div className="flex gap-2 flex-wrap">
                    <span className="text-gray-500">[{log.timestamp}]</span>
                    {log.source && (
                      <span className="text-xs bg-gray-700 px-1.5 py-0.5 rounded text-gray-400">
                        {log.source}
                      </span>
                    )}
                    <span className={`font-bold ${
                      log.type === 'error' ? 'text-red-400' :
                      log.type === 'warn' ? 'text-yellow-400' :
                      'text-blue-400'
                    }`}>
                      {log.type.toUpperCase()}:
                    </span>
                  </div>
                  <div className="ml-2 mt-1 whitespace-pre-wrap break-all">
                    {log.message}
                  </div>
                </div>
              ))
            )}
            <div ref={logsEndRef} />
          </div>
        </div>
      )}
    </>
  );
};

export default AdminDevTools;
